import { Card, Form, FormButtonRow, FormGroup, IconButton, RadioButton, RadioGroup } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { IReduxState } from '@frontend/configureStore';
import type { ValidationErrors } from '@frontend/utils';
import { PractitionerRole } from '@common/enums/practitionerRole';
import { employmentRoleAddEditSchema } from '@common/validation/employmentRole';
import { FormParagraphFormGroup } from '@frontend/components/fields/FormParagraphField';
import { CancelButton } from '@frontend/components/userDataManagement/CancelButton';
import { SaveButton } from '@frontend/components/userDataManagement/SaveButton';
import { getSelectedLanguage } from '@frontend/translate';
import { emptyValidationErrors, getValidationErrorTranslation, useAbortableFetch, validate } from '@frontend/utils';

interface IOrganizationPayoutManagerSettingsProps {
    employmentId: string;
    isReadOnly: boolean;
}

export interface IEmploymentRoleAddEdit {
    deactivate: boolean;
    practitionerRole: PractitionerRole;
}

export function OrganizationPayoutManagerSettings(props: IOrganizationPayoutManagerSettingsProps) {
    const abortableFetch = useAbortableFetch();
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const [practitionerIsOrganizationPayoutManager, setPractitionerIsOrganizationPayoutManager] =
        useState<boolean>(false);
    const [originalPractitionerIsOrganizationPayoutManager, setOriginalPractitionerIsOrganizationPayoutManager] =
        useState<boolean>(false);
    const isManager = useSelector((state: IReduxState) => state.authentication.isManager);

    useEffect(() => {
        if (isManager) {
            loadPractitionerIsOrganizationPayoutManager();
        }
    }, [props.employmentId]);

    if (!isManager) {
        return null;
    }

    return (
        <Card>
            <Card.Transition type={isEditing ? 'edit' : 'read'} />
            <Card.Header
                action={
                    !props.isReadOnly && !isEditing ? (
                        <IconButton
                            icon="edit-light"
                            labelText="edit"
                            onClick={() => setIsEditing(true)}
                        />
                    ) : undefined
                }
                title={getSelectedLanguage().employment.organizationManager}
            />
            <Card.Body>{!isEditing ? renderRead() : renderAddEdit()}</Card.Body>
        </Card>
    );

    function renderRead() {
        return (
            <FormParagraphFormGroup
                label={getSelectedLanguage().employment.organizationManager}
                tooltipText={getSelectedLanguage().employment.organizationManagerExplanation}
            >
                {originalPractitionerIsOrganizationPayoutManager
                    ? getSelectedLanguage().common.yes
                    : getSelectedLanguage().common.no}
            </FormParagraphFormGroup>
        );
    }

    function renderAddEdit() {
        return (
            <Form submitOnEnter={false}>
                <FormGroup
                    hasErrors={errors.has('employmentRoleAddEdit')}
                    label={getSelectedLanguage().employment.organizationManager}
                    tooltipText={getSelectedLanguage().employment.organizationManagerExplanation}
                >
                    <RadioGroup
                        error={getValidationErrorTranslation(errors.get('employmentRoleAddEdit'))}
                        layout="row"
                        name="employment.isOrganizationManager"
                        onChange={(event) => {
                            setErrors(emptyValidationErrors);
                            setPractitionerIsOrganizationPayoutManager(event.target.value === 'true');
                        }}
                        value={practitionerIsOrganizationPayoutManager.toString()}
                    >
                        <RadioButton
                            label={getSelectedLanguage().common.yes}
                            value="true"
                        />
                        <RadioButton
                            label={getSelectedLanguage().common.no}
                            value="false"
                        />
                    </RadioGroup>
                </FormGroup>
                <FormButtonRow>
                    <CancelButton
                        disabled={isSaving}
                        onCancel={async () => handleCancel()}
                    />
                    <SaveButton
                        disabled={isSaving}
                        onSave={handleSubmit}
                    />
                </FormButtonRow>
            </Form>
        );
    }

    async function loadPractitionerIsOrganizationPayoutManager() {
        const url = `/api/employments/${props.employmentId}/hasPractitionerRole?practitionerRole=${PractitionerRole.organizationPayoutManager}&hasEmploymentRoleSettingEnabled=true`;
        const result = await abortableFetch.fetchJson(url, 'GET');
        setOriginalPractitionerIsOrganizationPayoutManager(result.json.hasPractitionerRole);
        setPractitionerIsOrganizationPayoutManager(result.json.hasPractitionerRole);
    }

    function handleCancel() {
        setPractitionerIsOrganizationPayoutManager(originalPractitionerIsOrganizationPayoutManager);
        setErrors(emptyValidationErrors);
        setIsEditing(false);
    }

    async function handleSubmit() {
        setIsSaving(true);

        if (practitionerIsOrganizationPayoutManager !== originalPractitionerIsOrganizationPayoutManager) {
            const employmentRoleAddEdit: IEmploymentRoleAddEdit = {
                deactivate: !practitionerIsOrganizationPayoutManager,
                practitionerRole: PractitionerRole.organizationPayoutManager,
            };

            const { errors: validationErrors, item: employmentRoleAddEditValidated } = validate(
                employmentRoleAddEditSchema,
                employmentRoleAddEdit
            );

            if (validationErrors.size > 0) {
                setErrors(errors.set('employmentRoleAddEdit', 'any_invalid'));
            } else {
                const url = `/api/employments/${props.employmentId}/employmentRole`;
                await abortableFetch.fetchJson(url, 'POST', employmentRoleAddEditValidated);
                setOriginalPractitionerIsOrganizationPayoutManager(practitionerIsOrganizationPayoutManager);
                setErrors(emptyValidationErrors);
                setIsEditing(false);
            }
        } else {
            setIsEditing(false);
        }

        setIsSaving(false);
    }
}
